import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { ComposedChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
         ReferenceArea, ReferenceLine, Legend } from "recharts";
import { api, apiError, fmtDateTime } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Download, Printer, AlertTriangle, TrendingUp, Thermometer } from "lucide-react";

const RANGES = [30, 90, 365];

const download = async (kind, days, equipmentId, name) => {
  try {
    const res = await api.get(`/equipment/temperature-trends/export.${kind}`, {
      params: { days, ...(equipmentId ? { equipment_id: equipmentId } : {}) },
      responseType: "blob",
    });
    const url = URL.createObjectURL(res.data);
    if (kind === "pdf") {
      window.open(url, "_blank");
    } else {
      const a = document.createElement("a");
      a.href = url;
      a.download = `pathqity_temperature_${name || "all"}_${days}d.csv`;
      a.click();
    }
    setTimeout(() => URL.revokeObjectURL(url), 30000);
  } catch (e) { toast.error(apiError(e)); }
};

const Dot = ({ cx, cy, payload }) => {
  if (cx === null || cy === null || cx === undefined || cy === undefined) return null;
  const bad = payload?.out_of_range;
  return <circle cx={cx} cy={cy} r={bad ? 4.5 : 2.8} fill={bad ? "#b91c1c" : "#0f766e"}
                 stroke="#fff" strokeWidth={bad ? 1.5 : 0.8} />;
};

const ChartTip = ({ active, payload, unit }) => {
  if (!active || !payload?.length) return null;
  const p = payload[0].payload;
  return (
    <div className="border border-slate-300 bg-white p-2 text-xs shadow-lg dark:border-slate-600"
         data-testid="temp-chart-tooltip">
      <div className="font-semibold text-slate-900">{p.temperature}{unit}
        {p.out_of_range && <span className="ml-1 font-bold text-red-700">out of range</span>}
      </div>
      <div className="mt-0.5 text-slate-600">{fmtDateTime(p.at)}</div>
      <div className="text-slate-600">Recorded by <span className="font-medium text-slate-800">{p.by || "—"}</span></div>
      {p.task && <div className="text-slate-500">{p.task}</div>}
      {p.moving_avg !== null && <div className="text-amber-700">7-day avg {p.moving_avg}{unit}</div>}
      {p.comments && <div className="mt-0.5 max-w-[220px] text-slate-500">{p.comments}</div>}
    </div>
  );
};

const Stat = ({ label, value, tone = "text-slate-900", testid }) => (
  <div className="border border-slate-200 bg-white px-2 py-1.5" data-testid={testid}>
    <div className="text-[10px] font-semibold uppercase tracking-wide text-slate-500">{label}</div>
    <div className={`num text-sm font-bold ${tone}`}>{value}</div>
  </div>
);

export const TemperatureChart = ({ group, compact = false }) => {
  const { stats: s, temp_unit: unit, temp_min: lo, temp_max: hi, readings } = group;
  if (!readings.length) {
    return (
      <p className="text-sm text-slate-500" data-testid={`temp-empty-${group.equipment_id}`}>
        No temperature readings logged in this period.
      </p>
    );
  }
  const data = readings.map((r) => ({ ...r, label: r.at.slice(5, 10) }));
  const bounds = [...readings.map((r) => r.temperature), ...[lo, hi].filter((v) => v !== null)];
  const pad = Math.max((Math.max(...bounds) - Math.min(...bounds)) * 0.15, 0.5);
  const domain = [Math.min(...bounds) - pad, Math.max(...bounds) + pad];
  return (
    <div data-testid={`temp-chart-${group.equipment_id}`}>
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-6">
        <Stat label="Readings" value={s.count} testid="temp-stat-count" />
        <Stat label="Low" value={`${s.min}${unit}`} testid="temp-stat-min" />
        <Stat label="High" value={`${s.max}${unit}`} testid="temp-stat-max" />
        <Stat label="Average" value={`${s.avg}${unit}`} testid="temp-stat-avg" />
        <Stat label="Out of range" value={s.out_of_range}
              tone={s.out_of_range ? "text-red-800" : "text-slate-900"} testid="temp-stat-oor" />
        <Stat label="Drift / day"
              value={s.drift_per_day === null ? "—" : `${s.drift_per_day > 0 ? "+" : ""}${s.drift_per_day}${unit}`}
              tone={s.drifting ? "text-amber-700" : "text-slate-900"} testid="temp-stat-drift" />
      </div>

      {s.drifting && (
        <p className="mt-2 flex items-center gap-1.5 border-l-4 border-amber-400 bg-amber-50 px-2 py-1.5 text-xs text-amber-900"
           data-testid={`temp-drift-alert-${group.equipment_id}`}>
          <TrendingUp size={13} /> Trending {s.drift_per_day > 0 ? "warmer" : "cooler"} by about{" "}
          {Math.abs(s.drift_per_day)}{unit} per day across this period — worth a look before it leaves the range.
        </p>
      )}

      <div className="mt-3" style={{ height: compact ? 190 : 280 }}>
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart data={data} margin={{ top: 6, right: 10, left: -18, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
            {lo !== null && hi !== null && (
              <ReferenceArea y1={lo} y2={hi} fill="#14b8a6" fillOpacity={0.12} stroke="none" />
            )}
            {lo !== null && <ReferenceLine y={lo} stroke="#0f766e" strokeDasharray="4 3" />}
            {hi !== null && <ReferenceLine y={hi} stroke="#0f766e" strokeDasharray="4 3" />}
            <XAxis dataKey="label" tick={{ fontSize: 10, fill: "#64748b" }} minTickGap={24} />
            <YAxis tick={{ fontSize: 10, fill: "#64748b" }} domain={domain} allowDecimals
                   tickFormatter={(v) => `${Math.round(v * 10) / 10}${unit}`} width={62} />
            <Tooltip content={<ChartTip unit={unit} />} />
            {!compact && <Legend wrapperStyle={{ fontSize: 11 }} />}
            <Line type="monotone" dataKey="temperature" name="Reading" stroke="#0f766e"
                  strokeWidth={1.8} dot={<Dot />} activeDot={{ r: 5 }} isAnimationActive={false} />
            <Line type="monotone" dataKey="moving_avg" name="7-day average" stroke="#b45309"
                  strokeWidth={1.4} strokeDasharray="5 3" dot={false} isAnimationActive={false} />
          </ComposedChart>
        </ResponsiveContainer>
      </div>

      <p className="mt-1 text-[11px] text-slate-500">
        Shaded band is the acceptable range{lo !== null && hi !== null ? ` (${lo} to ${hi} ${unit})` : ""} ·
        red points are out of range · hover any point to see who logged it ·
        latest {s.latest}{unit} on {fmtDateTime(s.latest_at)} by {s.latest_by || "—"}
      </p>
    </div>
  );
};

/** Single-instrument panel with its own range toggles and exports. */
export default function TemperatureTrend({ equipmentId, equipmentName }) {
  const [days, setDays] = useState(30);
  const [group, setGroup] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/equipment/temperature-trends",
                                     { params: { days, equipment_id: equipmentId } });
      setGroup(data.instruments[0] || null);
    } catch (e) { toast.error(apiError(e)); }
    setLoading(false);
  }, [days, equipmentId]);

  useEffect(() => { load(); }, [load]);

  return (
    <div data-testid="temperature-trend-panel">
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <div className="flex items-center gap-1 text-xs font-semibold uppercase tracking-wide text-slate-600">
          <Thermometer size={14} /> Range
        </div>
        <div className="flex border border-slate-300">
          {RANGES.map((d) => (
            <button key={d} type="button" onClick={() => setDays(d)}
                    data-testid={`temp-range-${d}`}
                    className={`px-3 py-1.5 text-xs font-semibold transition-colors ${
                      days === d ? "bg-[#134e4a] text-white" : "bg-white text-slate-700 hover:bg-slate-50"}`}>
              {d === 365 ? "1 year" : `${d} days`}
            </button>
          ))}
        </div>
        <div className="ml-auto flex gap-2">
          <Button variant="outline" size="sm" className="h-9 border-slate-300"
                  data-testid="temp-export-csv"
                  onClick={() => download("csv", days, equipmentId, equipmentName)}>
            <Download size={14} className="mr-1.5" /> CSV
          </Button>
          <Button variant="outline" size="sm" className="h-9 border-slate-300"
                  data-testid="temp-export-pdf"
                  onClick={() => download("pdf", days, equipmentId, equipmentName)}>
            <Printer size={14} className="mr-1.5" /> PDF
          </Button>
        </div>
      </div>

      {loading ? <p className="text-sm text-slate-500" data-testid="temp-loading">Loading readings…</p>
        : !group ? <p className="text-sm text-slate-500">No temperature data for this instrument.</p>
          : <TemperatureChart group={group} />}

      {group && group.temp_min === null && group.temp_max === null && (
        <p className="mt-2 flex items-center gap-1.5 text-xs text-amber-800" data-testid="temp-no-range-warning">
          <AlertTriangle size={13} /> No acceptable range is set on this instrument, so readings cannot be
          flagged out of range. Add a min and max in the instrument record.
        </p>
      )}
    </div>
  );
}

export { RANGES, download as downloadTemperatureExport };
